/**
 * The address bar, as React state.
 *
 * `parseRoute` and `pathFor` are in `src/lib/route.ts` and know nothing about
 * a browser; this is the thin layer that points them at the real one.
 */

import { useCallback, useEffect, useState } from 'react'
import { parseRoute, pathFor, screenKey, type Route } from './lib/route'

export { parseRoute, pathFor, screenKey, type Route }

/**
 * The current route, and a way to change it that leaves the back button
 * working. `popstate` is what makes going back a real navigation rather than
 * a page that has stopped listening.
 */
export function useRoute(): { route: Route; navigate: (to: Route) => void } {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname))

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback((to: Route) => {
    window.history.pushState(null, '', pathFor(to))
    setRoute(to)
    window.scrollTo(0, 0)
  }, [])

  return { route, navigate }
}
